import { useEffect } from 'react'
import { CheckCircle, XCircle, AlertCircle, X, Info } from 'lucide-react'

const ICONS = {
  success: CheckCircle,
  error:   XCircle,
  warning: AlertCircle,
  info:    Info,
}

const STYLES = {
  success: 'bg-green-50 border-green-200 text-green-800',
  error:   'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info:    'bg-blue-50 border-blue-200 text-blue-800',
}

const ICON_STYLES = {
  success: 'text-green-500',
  error:   'text-red-500',
  warning: 'text-amber-500',
  info:    'text-blue-500',
}

/**
 * Single toast notification.
 * @param {object}   props
 * @param {string}   props.id
 * @param {'success'|'error'|'warning'|'info'} props.type
 * @param {string}   props.message
 * @param {function} props.onClose
 * @param {number}   [props.duration]  — ms, default 4000
 */
export function Toast({ id, type = 'info', message, onClose, duration = 4000 }) {
  const Icon = ICONS[type] ?? Info

  // Guarded: ToastContainer accepts the dismiss handler under either onClose
  // or onRemove, so a caller passing some third name leaves this undefined.
  // Unguarded, the timer then throws instead of dismissing and the toast sits
  // on screen forever — with the × button equally dead, since it calls the same
  // handler. Failing to auto-dismiss should degrade to "stays until clicked",
  // never to a stuck overlay.
  useEffect(() => {
    if (typeof onClose !== 'function') {
      console.warn('Toast: no dismiss handler — pass onRemove={removeToast} to ToastContainer.')
      return
    }
    const timer = setTimeout(() => onClose(id), duration)
    return () => clearTimeout(timer)
  }, [id, duration, onClose])

  return (
    <div
      className={[
        'flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg text-sm max-w-sm w-full',
        'animate-in slide-in-from-right duration-300',
        STYLES[type],
      ].join(' ')}
      role="alert"
    >
      <Icon size={16} className={`flex-shrink-0 mt-0.5 ${ICON_STYLES[type]}`} />
      <p className="flex-1">{message}</p>
      <button
        onClick={() => onClose(id)}
        className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  )
}

/**
 * Toast container — renders all active toasts.
 */
export function ToastContainer({ toasts, onClose, onRemove }) {
  if (!toasts?.length) return null
  const dismiss = onClose ?? onRemove
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 items-end">
      {toasts.map((t) => (
        <Toast key={t.id} {...t} onClose={dismiss} />
      ))}
    </div>
  )
}

/**
 * useToast — returns { toasts, addToast, removeToast }
 * Use useState in the component that owns the ToastContainer.
 */
import { useState, useCallback } from 'react'

export function useToast() {
  const [toasts, setToasts] = useState([])

  const addToast = useCallback(({ type = 'info', message, duration }) => {
    const id = `${Date.now()}-${Math.random()}`
    setToasts((prev) => [...prev, { id, type, message, duration }])
    return id
  }, [])

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return { toasts, addToast, removeToast }
}
